import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { catchError, EMPTY, from, map, of, switchMap, take, tap } from 'rxjs';
import { LocalizationService } from '@capital-access/common/localization';
import { FireflyModalService, NotificationService } from '@capital-access/firefly/components';
import { COMMON_COMPONENTS_LOCALIZATION_SCOPE } from '../../common-components.const';
import { SaveFilterCriteria } from './models/save-filter-criteria.model';
import { SavedFilterType } from './models/saved-filter-type.model';
import { CriteriaNamesItem, SavedFilters } from './models/saved-filters.model';
import { SavedFiltersRepository } from './repository/saved-filters-repository';

interface SavedFiltersSuggesterState {
  savedFiltersSuggestions: CriteriaNamesItem[];
  selectedFilter: CriteriaNamesItem | null;
  selectedFilterCriteria: SaveFilterCriteria | null;
  isFetchErrorInSavedFilters: boolean;
}

const initialState: SavedFiltersSuggesterState = {
  savedFiltersSuggestions: [],
  selectedFilter: null,
  selectedFilterCriteria: null,
  isFetchErrorInSavedFilters: false
};

export const SavedFiltersSingleSuggesterStore = signalStore(
  withState<SavedFiltersSuggesterState>(initialState),
  withMethods(store => {
    const savedFiltersRepository = inject(SavedFiltersRepository);
    const destroyRef = inject(DestroyRef);
    const modal = inject(FireflyModalService);
    const notificationService = inject(NotificationService);
    const localizationService = inject(LocalizationService);

    return {
      setInitialData: (filterType: SavedFilterType) => {
        savedFiltersRepository
          .getSavedFilters(filterType)
          .pipe(
            map((filters: SavedFilters) => {
              patchState(store, { savedFiltersSuggestions: filters.criteriaNames, isFetchErrorInSavedFilters: false });
            }),
            catchError(() => {
              patchState(store, { savedFiltersSuggestions: [], isFetchErrorInSavedFilters: true });

              return EMPTY;
            }),
            takeUntilDestroyed(destroyRef)
          )
          .subscribe();
      },

      setSelectedFilter(selectedFilter: CriteriaNamesItem | null): void {
        patchState(store, () => ({ selectedFilter }));
      },

      openDeleteFilterModal(selectedFilter: CriteriaNamesItem): void {
        localizationService
          .localize(
            [
              'deleteSavedFilter',
              'deleteFilterMessage',
              'delete',
              'cancel',
              'successfullyDeletedFilterMessage',
              'failedToDeleteFilterMessage'
            ],
            { filterName: selectedFilter.name },
            { scope: COMMON_COMPONENTS_LOCALIZATION_SCOPE }
          )
          .pipe(
            take(1),
            switchMap(([title, body, actionText, dismissText, successMessage, errorMessage]) =>
              from(
                modal.openConfirmation({
                  style: 'error',
                  title,
                  body,
                  actionText,
                  dismissText
                }).result
              ).pipe(
                catchError(() => EMPTY),
                switchMap(() => this.deleteSelectedFilter(selectedFilter, successMessage, errorMessage))
              )
            )
          )
          .subscribe();
      },

      deleteSelectedFilter(selectedFilter: CriteriaNamesItem, successMessage: string, errorMessage: string) {
        return savedFiltersRepository.deleteSavedFilter(selectedFilter.id).pipe(
          tap(() => {
            patchState(store, state => ({
              savedFiltersSuggestions: state.savedFiltersSuggestions.filter(f => f.id !== selectedFilter.id)
            }));
            notificationService.notify(successMessage, { type: 'success' });
          }),
          catchError(() => {
            notificationService.notify(errorMessage, { type: 'danger' });
            return EMPTY;
          })
        );
      },
      setSelectedSavedFilter(id: number) {
        return savedFiltersRepository.getSavedFilterCriteriaById(id).pipe(
          tap(criteria => patchState(store, { selectedFilterCriteria: criteria })),
          catchError(() => {
            patchState(store, { selectedFilterCriteria: null });
            localizationService
              .localize(['savedFilterFailedMessage'], {}, { scope: COMMON_COMPONENTS_LOCALIZATION_SCOPE })
              .pipe(
                take(1),
                tap(([message]) => notificationService.notify(message, { type: 'danger' }))
              )
              .subscribe();
            return EMPTY;
          })
        );
      }
    };
  })
);
